import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import "ol/ol.css";
import "ol-ext/dist/ol-ext.css";
import '@fortawesome/fontawesome-free/css/all.min.css';
import Map from 'ol/Map';
import View from 'ol/View';
import { defaults as defaultControls } from "ol/control";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import Dialog from "ol-ext/control/Dialog";
import { fromLonLat } from "ol/proj";
import { sendCoordinates } from '../services/api';
import logger from '../logger';
import ScaleLine from 'ol/control/ScaleLine';
import MousePosition from 'ol/control/MousePosition';
import { createStringXY } from 'ol/coordinate';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Point } from 'ol/geom';
import Feature from 'ol/Feature';
import { Style, Icon } from 'ol/style';
import SideBar from './SideBar';
import "../App.css";

const SharePosition = () => {
    const [location, setLocation] = useState({
        longitude: 11.028995965569194,
        latitude: 46.05321545463036,
    });

    const [loading, setLoading] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('access'));
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    // const [hoverLock, setHoverLock] = useState(false); // State to lock hover info
    // const [showDialog, setShowDialog] = useState(false);
    // const showDialogRef = useRef(false);  // Ref to track dialog state
    const [isButtonLoading, setButtonLoading] = useState(false);   
    const mapRef = useRef();
    const markerRef = useRef(null);

    const [isDrawerOpen, setDrawerOpen] = useState(true);
    const [hoveredButtonInfo, setHoveredButtonInfo] = useState('<strong>Hover over a button to see its details</strong>'); // State for hover information   
    const locationPath = useLocation(); // Get current route

    const isMobileDevice = () => /Mobi|Android/i.test(navigator.userAgent);

    const toggleVisibility = (isVisible) => {
        const infoContainer = document.querySelector('.information-container');
        const infoElement = document.getElementById('info-element');
        if (infoContainer) infoContainer.style.display = isVisible ? 'block' : 'none';
        if (infoElement) infoElement.style.display = isVisible ? 'block' : 'none';
    };

    const toggleDrawer = () => {
        setDrawerOpen((prevState) => {
            const newState = !prevState;
            //logger.log("is drawer open: ", newState);
            return newState;
        });
    };

    function makeDialogDraggable(dialogElement) {
        let isMouseDown = false;
        let offset = [0, 0];
    
        const handleMouseDown = (e) => {
            // Prevent dragging if interacting with form elements inside the dialog
            if (['INPUT', 'TEXTAREA', 'BUTTON', 'SELECT'].includes(e.target.tagName)) return;
    
            // Activate dragging
            isMouseDown = true;
    
            // Capture the mouse offset from the dialog's position
            offset = [
                dialogElement.offsetLeft - e.clientX,
                dialogElement.offsetTop - e.clientY,
            ];
    
            // Change cursor style to indicate dragging
            dialogElement.style.cursor = 'move';
        };
    
        const handleMouseMove = (e) => {
            if (!isMouseDown) return;
    
            // Calculate new position
            const mousePosition = {
                x: e.clientX,
                y: e.clientY,
            };
    
            // Move dialog to the new position
            dialogElement.style.left = `${mousePosition.x + offset[0]}px`;
            dialogElement.style.top = `${mousePosition.y + offset[1]}px`;
        };
    
        const handleMouseUp = () => {
            isMouseDown = false;
            dialogElement.style.cursor = 'default';
        };
    
        // Attach event listeners
        dialogElement.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousemove', handleMouseMove);
    
        // Cleanup function to remove event listeners
        return () => {
            dialogElement.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }

    const updateMarker = (longitude, latitude) => {
        if (markerRef.current && mapRef.current) {
            const newCoords = fromLonLat([longitude, latitude]);
            markerRef.current.getGeometry().setCoordinates(newCoords);
            mapRef.current.getView().setCenter(newCoords);
        };
    };   
    
    const handleDeviceSpecificDialog = () => {
        if (!mapRef.current) {
            console.error("Map is not initialized yet.");
            return;
        }
    
        // 🛑 Remove existing dialog if open
        const existingDialog = document.querySelector('.dialog-mobile, .dialog-desktop');
        if (existingDialog) {
            existingDialog.remove();
        }

        let geoWatchId; // Store geolocation watcher
    
        // ✅ Restart tracking each time dialog opens
        if (geoWatchId !== undefined) {
            navigator.geolocation.clearWatch(geoWatchId);
            geoWatchId = undefined;
        }        
    
        const startGeolocationTracking = () => {
            if (navigator.geolocation) {
                geoWatchId = navigator.geolocation.watchPosition(
                    (position) => {
                        const { latitude, longitude } = position.coords;
                        setLocation({ latitude, longitude });
                        updateMarker(longitude, latitude);
                    },
                    (error) => {
                        logger.error("Failed to fetch device location:", error);
                    }
                );
            }
        };
    
        const makeDialogDraggableSafe = () => {
            const dialogElement = document.querySelector('.dialog-mobile, .dialog-desktop');
            if (dialogElement) {
                makeDialogDraggable(dialogElement);
            }
        };
    
        if (isMobileDevice()) {
            const formHtml = `
                <div id="coordinates-mobile">                    
                    <label for="longitude">Longitude:</label>
                    <input type="text" id="longitude" placeholder="Enter Longitude">
                    
                    <label for="latitude">Latitude:</label>
                    <input type="text" id="latitude" placeholder="Enter Latitude">
                    
                    <button id="submitCoordinatesMobile">Submit</button>
                </div>
            `;
    
            const dialog = new Dialog({
                className: "dialog-mobile",
                content: formHtml,
                closeBox: false,
            });
    
            mapRef.current.addControl(dialog);
            toggleVisibility(false);
            dialog.show();
            setTimeout(makeDialogDraggableSafe, 0);
    
            const longitudeInput = document.querySelector('#longitude');
            const latitudeInput = document.querySelector('#latitude');
            const submitButtonMobile = document.querySelector('#submitCoordinatesMobile');
    
            startGeolocationTracking(); // ✅ Start tracking on opening
    
            // Update location in real time
            longitudeInput.addEventListener('input', (e) => {
                setLocation((prev) => {
                    const newLongitude = parseFloat(e.target.value) || 0;
                    updateMarker(newLongitude, prev.latitude);
                    return { ...prev, longitude: newLongitude };
                });
            });
    
            latitudeInput.addEventListener('input', (e) => {
                setLocation((prev) => {
                    const newLatitude = parseFloat(e.target.value) || 0;
                    updateMarker(prev.longitude, newLatitude);
                    return { ...prev, latitude: newLatitude };
                });
            });
    
            submitButtonMobile.addEventListener('click', async () => {
                if (location.longitude >= -180 && location.longitude <= 180 && location.latitude >= -90 && location.latitude <= 90) {
                    setButtonLoading(true);
                    submitButtonMobile.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
                    submitButtonMobile.disabled = true;
    
                    try {
                        await sendCoordinates({ lat: location.latitude, lon: location.longitude });
                        logger.log('Coordinates Sent Successfully');
                    } catch (error) {
                        logger.error("Failed to send coordinates:", error);
                    } finally {
                        setButtonLoading(false);
                        submitButtonMobile.innerHTML = 'Submit';
                        submitButtonMobile.disabled = false;
                        dialog.hide();
                        toggleVisibility(true);
                        navigator.geolocation.clearWatch(geoWatchId); // 🛑 Stop tracking on close
                        geoWatchId = undefined;
                    }
                } else {
                    alert("Please enter valid numbers for both Longitude and Latitude.");
                }
            });
        } else {
            const formHtml = `
                <div id="coordinates-desktop">
                    <button id="customCloseButton" style="position: absolute; top: 5px; right: 5px; background-color: transparent; color: white; font-size:16px; border: none; cursor: pointer;">
                        <i class="fas fa-times"></i>
                    </button>
                    <p style="color: red; font-weight: bold;">Note: This feature is intended for mobile devices. Please enter coordinates manually.</p>
                    <label for="longitude-input">Longitude:</label>
                    <input type="text" id="longitude-input" placeholder="Enter longitude" style="margin-bottom: 10px; width: 100%;">
                    <label for="latitude-input">Latitude:</label>
                    <input type="text" id="latitude-input" placeholder="Enter latitude" style="width: 100%;">
                    <br/><br/>
                    <button id="submitCoordinatesDesktop" style="cursor: pointer; padding: 5px 10px; border: 1px solid; border-radius: 5px;">Submit</button>
                </div>
            `;
    
            const dialog = new Dialog({
                className: "dialog-desktop",
                content: formHtml,
                closeBox: false,
            });
    
            mapRef.current.addControl(dialog);
            toggleVisibility(false);
            dialog.show();
            setTimeout(makeDialogDraggableSafe, 0);
    
            const longitudeInput = document.querySelector('#longitude-input');
            const latitudeInput = document.querySelector('#latitude-input');
            const submitButtonDesktop = document.querySelector('#submitCoordinatesDesktop');
            const customCloseButton = document.querySelector('#customCloseButton');
    
            startGeolocationTracking(); // ✅ Start tracking for desktop
    
            // Update location state in real time
            longitudeInput.addEventListener('input', (e) => {
                setLocation((prev) => ({ ...prev, longitude: parseFloat(e.target.value) || 0 }));
            });
    
            latitudeInput.addEventListener('input', (e) => {
                setLocation((prev) => ({ ...prev, latitude: parseFloat(e.target.value) || 0 }));
            });
    
            customCloseButton.addEventListener('click', () => {
                toggleVisibility(true);
                dialog.hide();
    
                if (geoWatchId !== undefined) {
                    navigator.geolocation.clearWatch(geoWatchId);
                    geoWatchId = undefined; // ✅ Stop tracking on close
                }
            });
    
            submitButtonDesktop.addEventListener('click', async function (event) {
                event.preventDefault();
    
                if (location.longitude >= -180 && location.longitude <= 180 && location.latitude >= -90 && location.latitude <= 90) {
                    setButtonLoading(true);
                    submitButtonDesktop.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
                    submitButtonDesktop.disabled = true;
    
                    try {
                        await sendCoordinates({ lat: location.latitude, lon: location.longitude });
                        logger.log("Coordinates sent successfully");
                    } catch (error) {
                        logger.error("Failed to send coordinates:", error);
                        alert("Error sending coordinates. Please try again.");
                    } finally {
                        setButtonLoading(false);
                        submitButtonDesktop.innerHTML = 'Submit';
                        submitButtonDesktop.disabled = false;
                        dialog.hide();
                        toggleVisibility(true);
                        mapRef.current.removeControl(dialog);
    
                        if (geoWatchId !== undefined) {
                            navigator.geolocation.clearWatch(geoWatchId); // 🛑 Stop tracking on close
                            geoWatchId = undefined;
                        }
                    }
                }
            });
        }
    };       

    useEffect(() => {
        if (isMapLoaded && locationPath.pathname === '/share-position') {
            handleDeviceSpecificDialog();
        }
    }, [isMapLoaded, locationPath]);    

    useEffect(() => {
        // Update marker position whenever longitude or latitude changes
        if (markerRef.current && mapRef.current) {
            const newCoords = fromLonLat([location.longitude, location.latitude]);
            markerRef.current.getGeometry().setCoordinates(newCoords);
            mapRef.current.getView().setCenter(newCoords); // Center the map on the new position
        }
    }, [location]);

    // Set up the map and add layers to the map after data is fetched
    useEffect(() => {

        setLoading(true);

        // Load saved BBOX from localStorage if available
        const savedBbox = localStorage.getItem('mapBbox');

        // Initialize the map only once
        if (!mapRef.current) {
            const map = new Map({
                target: "map",
                controls: defaultControls({ zoom: false }).extend([
                    new ScaleLine({
                        units: 'metric',
                        bar: true,
                        steps: 4,
                        text: true,
                        minWidth: 140,
                        maxWidth: 140,
                    }),
                    new MousePosition({
                        coordinateFormat: createStringXY(4), // Formats the coordinates to 4 decimal places
                        projection: 'EPSG:4326', // Set projection to WGS84 (latitude/longitude)
                        className: 'mouse-position', // Class name for styling
                        target: document.getElementById('mouse-position'), // The DOM element to display the mouse position
                        undefinedHTML: '&nbsp;', // What to display when the mouse is outside the map
                    })
                ]),
                layers: [
                    new TileLayer({
                        source: new OSM({ attributions: [] }),
                        title: "Map",
                        displayInLayerSwitcher: false,
                        displayInLayerSwitcherImage: true,
                    }),
                ],
                view: new View({
                    center: fromLonLat([location.longitude, location.latitude]),
                    zoom: 4,
                }),
            });

            // Create a vector layer for the marker
            const vectorLayer = new VectorLayer({
                source: new VectorSource(),
            });

            // Add marker feature to the vector layer
            const marker = new Feature({
                geometry: new Point(fromLonLat([location.longitude, location.latitude])),
            });

            // Set the style for the marker
            marker.setStyle(
                new Style({
                    image: new Icon({
                        anchor: [0.5, 1],
                        src: 'https://cdn-icons-png.flaticon.com/512/684/684908.png', // Example pin icon
                        scale: 0.05,
                    }),
                })
            );

            // Save reference to the marker
            markerRef.current = marker;

            // Add the marker to the vector layer source
            vectorLayer.getSource().addFeature(marker);

            // Add the vector layer to the map
            map.addLayer(vectorLayer);

            mapRef.current = map;

            // If a BBOX is saved, fit the map view to this BBOX
            if (savedBbox) {
                const bbox = JSON.parse(savedBbox);
                const extent = bbox.map(coord => parseFloat(coord)); // Parse the bbox array
                map.getView().fit(extent, { size: map.getSize() });
            };

            // Save the current BBOX to localStorage whenever the map is moved or zoomed
            map.on('moveend', () => {
                const extent = map.getView().calculateExtent(map.getSize());
                localStorage.setItem('mapBbox', JSON.stringify(extent));
            });

            setIsMapLoaded(true);
        };
      
        setLoading(false);

    }, []);

    return (
        <>
            <div id="map"></div>
            <SideBar
                mapRef={mapRef}
                isLoggedIn={isLoggedIn}
                setIsLoggedIn={setIsLoggedIn}
                setHoveredButtonInfo={setHoveredButtonInfo}            
                toggleDrawer={toggleDrawer}
            />            
            <div className={`information-container ${isDrawerOpen ? 'open' : ''}`}>
                <div className="title">
                    <h2>Instructions</h2>
                </div>
                <div className="information-content">
                    <p dangerouslySetInnerHTML={{ __html: hoveredButtonInfo }} />
                </div>
            </div>            
        </>
    );
};

export default SharePosition;
